import { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { Button } from '@shared/components/Button';
import { COPY_TOOLTIP_DURATION_MS } from '@shared/components/LinkActionButtons';
import { Menu } from '@shared/components/Menu';
import { useToast } from '@shared/components/Toast';
import { View } from '@shared/components/View';
import { MBTextInput } from '@shared/components/textInput';
import { TruncateLink } from '@shared/components/truncate';
import { UrlField } from '@shared/data/entity/fields/UrlField';
import {
  getComponentLabelWithFallback,
  getComponentPlaceholder,
} from '@shared/scenes/workflows/utils';
import { Colors, getAnchorName } from '@shared/styles';
import { copyToClipboard } from '@shared/util/clipboard';
import { useBoolean, useMountedRef } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { openLink } from '@shared/util/link';
import { addPrefixIfNeeded, validateUrl } from '@shared/util/validate/url';
import { EditFieldContainer } from '../EditFieldContainer';
import { useFieldDraft } from '../useFieldDraft';
import { UrlEditFieldProps } from './UrlEditFieldProps';

export function UrlEditFieldMobile({
  field,
  component,
  updateField,
  error,
  allowClear,
  style,
}: UrlEditFieldProps) {
  const fieldUrl = field?.url ?? '';
  const [draft, commitDraft] = useFieldDraft<string, string>(fieldUrl);
  const value = draft ?? fieldUrl;
  const hasValue = !isEmpty(value);
  const isReadOnly = !!component.readonly;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, openModal, closeModal] = useBoolean();
  const modalTitle = getComponentLabelWithFallback(component);
  const anchorName = getAnchorName('url-edit-field', component.id);
  const toast = useToast();
  const href = addPrefixIfNeeded(value);

  const openMenu = useCallback(() => {
    if (hasValue) {
      setIsMenuOpen(true);
    }
  }, [hasValue]);

  const openInBrowser = useCallback(() => {
    openLink(href);
  }, [href]);

  const copyLink = useCallback(() => {
    void copyToClipboard(href);
    toast({
      message: i18n('workflowTemplate.component.url.actionButtonsSheet.copiedToClipboard'),
    });
  }, [href, toast]);

  const updateValue = useCallback(
    (updatedValue: string) => {
      const url = updatedValue.trim();
      commitDraft(updatedValue, url);
      updateField(component.id, !isEmpty(url) ? new UrlField([{ url }]) : null);
    },
    [updateField, component.id, commitDraft],
  );

  // Normalize on close rather than in an effect keyed on `isModalOpen`, which also ran on
  // mount and could prefix (and so dirty) a stored value the user never touched.
  const handleCloseModal = useCallback(() => {
    const prefixedValue = addPrefixIfNeeded(value);
    if (prefixedValue !== value) {
      updateValue(prefixedValue);
    }
    closeModal();
  }, [closeModal, updateValue, value]);

  const onClear = useCallback(() => {
    updateValue('');
  }, [updateValue]);

  const showClearButton = hasValue && !isReadOnly && allowClear;
  const placeholder = getComponentPlaceholder(component) ?? '';
  const previewText = value || placeholder;
  const placeholderStyle = { color: Colors.v2.gray50 };

  return (
    <EditFieldContainer
      component={component}
      showLabel={true}
      error={error}
      showClearButton={showClearButton}
      onClear={onClear}
      style={style}
      anchorName={anchorName}
      printContent={field?.url || undefined}
    >
      <View onClick={isReadOnly ? openMenu : openModal} onLongPress={openMenu}>
        <View paddingRight={2}>
          <TruncateLink
            href={previewText}
            showProtocol={true}
            style={!hasValue ? placeholderStyle : undefined}
          />
        </View>
        {isModalOpen && (
          <UrlEditSheet
            value={value}
            setValue={updateValue}
            closeModal={handleCloseModal}
            modalTitle={modalTitle}
            placeholder={placeholder}
            anchorName={anchorName}
          />
        )}
      </View>
      <Menu isOpen={isMenuOpen} onOpenChange={setIsMenuOpen} anchorName={anchorName}>
        <Menu.Content>
          <Menu.Note icon={false}>{value}</Menu.Note>
          <Menu.Item
            label={i18n('workflowTemplate.component.url.actionButtonsSheet.openInBrowser')}
            onClick={openInBrowser}
          />
          <Menu.Item
            label={i18n('workflowTemplate.component.url.actionButtonsSheet.copyLink')}
            onClick={copyLink}
          />
        </Menu.Content>
      </Menu>
    </EditFieldContainer>
  );
}

function UrlEditSheet({
  value,
  setValue,
  closeModal,
  modalTitle,
  placeholder,
  anchorName,
}: {
  value: string;
  setValue: (value: string) => void;
  closeModal: VoidFunction;
  modalTitle: string;
  placeholder?: string;
  anchorName: string;
}) {
  return (
    <AnchoredSheet
      onClose={closeModal}
      anchorName={anchorName}
      renderTitleBar={() => {
        return (
          <AnchoredSheetTitleBar
            onRightIconPress={closeModal}
            title={modalTitle}
            rightIcon="exit"
            rightIconColor={Colors.v2.purple}
            rightIconGridSize={4}
          />
        );
      }}
    >
      <View padding={2}>
        <MBTextInput
          multiline={true}
          inputMode="url"
          textContentType="URL"
          keyboardType="url"
          value={value}
          onChangeText={setValue}
          placeholder={placeholder}
          autoCapitalize="none"
          autoFocus={true}
          style={{
            fontSize: 16,
            lineHeight: 24,
            backgroundColor: Colors.v2.gray05,
            maxHeight: 250,
          }}
        />
        <UrlEditSheetButtons value={value} />
      </View>
    </AnchoredSheet>
  );
}

function UrlEditSheetButtons({ value }: { value: string }) {
  const mounted = useMountedRef();
  const openLinkLabel = i18n('workflowTemplate.component.url.actionButtonsSheet.openInBrowser');
  const copyLinkLabel = i18n('linkActions.copyToClipboard');
  const copiedLinkLabel = i18n('linkActions.copiedToClipboard');
  const [copyButtonLabel, setCopyButtonLabel] = useState(copyLinkLabel);
  const href = addPrefixIfNeeded(value);
  const isValid = validateUrl(href);

  const onClickCopy = () => {
    void copyToClipboard(href);
    setCopyButtonLabel(copiedLinkLabel);
    setTimeout(() => {
      if (mounted.current) {
        setCopyButtonLabel(copyLinkLabel);
      }
    }, COPY_TOOLTIP_DURATION_MS);
  };

  const openUrl = () => {
    openLink(href);
  };

  const opacity = isValid ? 1 : 0.3;

  return (
    <View row paddingTop={3} style={{ justifyContent: 'space-between', opacity }}>
      <View flex style={{ flexBasis: '55%' }}>
        <Button
          variant="tertiary"
          size="large"
          icon="link"
          label={openLinkLabel}
          onClick={isValid ? openUrl : undefined}
        />
      </View>
      <View flex style={{ flexBasis: '5%' }} />
      <View flex style={{ flexBasis: '40%' }}>
        <Button
          variant="tertiary"
          size="large"
          icon="squares-stacked"
          label={copyButtonLabel}
          onClick={isValid ? onClickCopy : undefined}
        />
      </View>
    </View>
  );
}
